/**
 * Label Command — in-flight dedup + LRU GRF cache 단위 테스트.
 *
 * puppeteer 의존성은 _setBuildOverride 로 mock. 검증 대상:
 *   1) cacheKey — 같은 입력 같은 키 / 다른 입력 다른 키 / 정규화
 *   2) cache hit — 같은 (template, data, ...) 반복 호출 시 puppeteer 0 회
 *   3) in-flight dedup — 동일 요청 동시 진행 시 build 함수 1 회만, 모든 caller 가 같은 결과
 *   4) cache TTL — 만료된 entry 는 새로 build
 *   5) LRU eviction — 캐시 가득 차면 오래된 것부터 제거
 *   6) build 실패 시 캐시 안 됨 → 다음 호출에서 재시도
 *   7) id 와 model 별 다른 키 공간
 */

// import 시점에 끌려오는 무거운 deps (headless pool, headless model) 은 어차피
// _setBuildOverride 로 교체하므로 module load 만 가능하게 mock.
jest.mock('./headless-pool-for-label', () => ({
  getHeadlessPool: () => ({ acquire: jest.fn(), release: jest.fn() })
}))
jest.mock('./headless-model', () => ({
  headlessModel: async () => ({ model: {} })
}))

import {
  fnv1a,
  cacheKey,
  labelcommand,
  _setBuildOverride,
  _resetLabelState,
  getLabelCacheStats,
  type LabelCommandOptions
} from './label-command'

const ctx = { state: { domain: { subdomain: 'test' }, user: { id: 'u1' } } }

// ─── fnv1a ──────────────────────────────────────────────────────────────────

describe('label fnv1a hash', () => {
  test('결정성', () => {
    expect(fnv1a('foo')).toBe(fnv1a('foo'))
  })

  test('충돌 적음 — 50 개 입력', () => {
    const hashes = new Set<string>()
    for (let i = 0; i < 50; i++) {
      hashes.add(fnv1a(`label-${i}-{"data":${i}}`))
    }
    expect(hashes.size).toBe(50)
  })
})

// ─── cacheKey ───────────────────────────────────────────────────────────────

describe('cacheKey 생성', () => {
  test('같은 입력 → 같은 key', () => {
    const opts: LabelCommandOptions = { id: 'lbl1', data: { x: 1 }, orientation: 'N', context: ctx }
    expect(cacheKey(opts)).toBe(cacheKey(opts))
  })

  test('id 다르면 다른 key', () => {
    const a = cacheKey({ id: 'lbl1', data: { x: 1 }, context: ctx })
    const b = cacheKey({ id: 'lbl2', data: { x: 1 }, context: ctx })
    expect(a).not.toBe(b)
  })

  test('draft / released 분리', () => {
    const a = cacheKey({ id: 'lbl1', data: {}, draft: false, context: ctx })
    const b = cacheKey({ id: 'lbl1', data: {}, draft: true, context: ctx })
    expect(a).not.toBe(b)
  })

  test('orientation 다르면 다른 key', () => {
    const a = cacheKey({ id: 'lbl1', data: {}, orientation: 'N', context: ctx })
    const b = cacheKey({ id: 'lbl1', data: {}, orientation: 'R', context: ctx })
    expect(a).not.toBe(b)
  })

  test('mirror / upsideDown flag 가 key 에 반영', () => {
    const base = { id: 'lbl1', data: {}, context: ctx }
    const a = cacheKey(base)
    const b = cacheKey({ ...base, mirror: true })
    const c = cacheKey({ ...base, upsideDown: true })
    const d = cacheKey({ ...base, mirror: true, upsideDown: true })

    expect(new Set([a, b, c, d]).size).toBe(4)
  })

  test('data 변경 시 다른 key', () => {
    const a = cacheKey({ id: 'lbl1', data: { sn: 'A1' }, context: ctx })
    const b = cacheKey({ id: 'lbl1', data: { sn: 'A2' }, context: ctx })
    expect(a).not.toBe(b)
  })

  test('data 가 deep 동일하면 같은 key (key 순서 무관)', () => {
    const a = cacheKey({ id: 'lbl1', data: { x: 1, y: 2 }, context: ctx })
    const b = cacheKey({ id: 'lbl1', data: { x: 1, y: 2 }, context: ctx })
    expect(a).toBe(b)
  })

  test('id 와 model 직접 전달은 다른 key 공간', () => {
    const a = cacheKey({ id: 'lbl1', data: {}, context: ctx })
    const b = cacheKey({ model: { template: 'abc' }, data: {}, context: ctx })
    expect(a).not.toBe(b)
  })

  test('model JSON 내용이 같으면 같은 key (id 없을 때)', () => {
    const a = cacheKey({ model: { width: 100 }, data: {}, context: ctx })
    const b = cacheKey({ model: { width: 100 }, data: {}, context: ctx })
    expect(a).toBe(b)
  })
})

// ─── labelcommand: cache + dedup ────────────────────────────────────────────

describe('labelcommand — cache + in-flight dedup', () => {
  let buildCalls: LabelCommandOptions[]
  let nextResult: string | undefined
  let buildShouldFail: boolean
  let buildDelay: number

  beforeEach(() => {
    buildCalls = []
    nextResult = '^XA\n^GFA,abc\n^FS\n^XZ'
    buildShouldFail = false
    buildDelay = 0

    _setBuildOverride(async opts => {
      buildCalls.push(opts)
      if (buildDelay > 0) {
        await new Promise(r => setTimeout(r, buildDelay))
      }
      if (buildShouldFail) throw new Error('build failed')
      return nextResult
    })
    _resetLabelState()
  })

  afterEach(() => {
    _setBuildOverride(null)
    _resetLabelState()
  })

  test('첫 호출 → build 1 회, 결과 반환', async () => {
    const result = await labelcommand({ id: 'lbl1', data: {}, context: ctx })
    expect(buildCalls).toHaveLength(1)
    expect(result).toBe('^XA\n^GFA,abc\n^FS\n^XZ')
  })

  test('동일 요청 두 번 sequential → 두 번째는 cache hit', async () => {
    await labelcommand({ id: 'lbl1', data: { sn: 'A1' }, context: ctx })
    expect(buildCalls).toHaveLength(1)

    const second = await labelcommand({ id: 'lbl1', data: { sn: 'A1' }, context: ctx })
    expect(buildCalls).toHaveLength(1)  // cache hit
    expect(second).toBe('^XA\n^GFA,abc\n^FS\n^XZ')
  })

  test('동일 요청 동시 100 회 → in-flight dedup 으로 build 1 회', async () => {
    buildDelay = 50

    const promises = Array.from({ length: 100 }, () =>
      labelcommand({ id: 'lbl1', data: { sn: 'A1' }, context: ctx })
    )

    const results = await Promise.all(promises)
    expect(buildCalls).toHaveLength(1)
    // 모든 caller 가 같은 결과
    expect(new Set(results).size).toBe(1)
  })

  test('다른 요청은 별도 build', async () => {
    await labelcommand({ id: 'lbl1', data: { sn: 'A1' }, context: ctx })
    await labelcommand({ id: 'lbl1', data: { sn: 'A2' }, context: ctx })
    await labelcommand({ id: 'lbl2', data: { sn: 'A1' }, context: ctx })

    expect(buildCalls).toHaveLength(3)
  })

  test('build 실패 시 cache 안 됨 → 다음 호출에서 재시도', async () => {
    buildShouldFail = true
    await expect(
      labelcommand({ id: 'lbl1', data: {}, context: ctx })
    ).rejects.toThrow('build failed')
    expect(buildCalls).toHaveLength(1)

    buildShouldFail = false
    const result = await labelcommand({ id: 'lbl1', data: {}, context: ctx })
    expect(buildCalls).toHaveLength(2)
    expect(result).toBe('^XA\n^GFA,abc\n^FS\n^XZ')
  })

  test('build undefined 반환 시 cache 안 됨', async () => {
    nextResult = undefined
    const r1 = await labelcommand({ id: 'lbl1', data: {}, context: ctx })
    expect(r1).toBeUndefined()
    expect(buildCalls).toHaveLength(1)

    // 같은 입력 다시 — cache 없으므로 또 build
    const r2 = await labelcommand({ id: 'lbl1', data: {}, context: ctx })
    expect(buildCalls).toHaveLength(2)
  })

  test('orientation 다르면 별도 cache', async () => {
    await labelcommand({ id: 'lbl1', data: {}, orientation: 'N', context: ctx })
    await labelcommand({ id: 'lbl1', data: {}, orientation: 'R', context: ctx })
    await labelcommand({ id: 'lbl1', data: {}, orientation: 'N', context: ctx })

    expect(buildCalls).toHaveLength(2)  // N, R 각각 1 회. N 의 두 번째는 cache hit
  })

  test('대량 인쇄 시나리오 — 같은 라벨 200 장 출력 시 build 1 회', async () => {
    const params = { id: 'lbl-mass', data: { common: 'data' }, orientation: 'N', context: ctx }

    for (let i = 0; i < 200; i++) {
      await labelcommand(params)
    }

    expect(buildCalls).toHaveLength(1)
  })

  test('각 라벨이 다른 시리얼 번호인 시나리오 — 매번 build', async () => {
    for (let i = 0; i < 10; i++) {
      await labelcommand({
        id: 'lbl-serial',
        data: { sn: `SN-${i}` },
        context: ctx
      })
    }
    expect(buildCalls).toHaveLength(10)
  })

  test('cache stats 가 합리적 값 반환', async () => {
    const before = getLabelCacheStats()
    expect(before.cacheSize).toBe(0)

    await labelcommand({ id: 'lbl1', data: { x: 1 }, context: ctx })
    await labelcommand({ id: 'lbl2', data: { x: 1 }, context: ctx })

    const after = getLabelCacheStats()
    expect(after.cacheSize).toBe(2)
    expect(after.inFlight).toBe(0)  // 완료 후
  })

  test('in-flight 동안 stats.inFlight 증가', async () => {
    buildDelay = 100

    const promise = labelcommand({ id: 'lbl1', data: {}, context: ctx })
    // 시작 직후 — buildDelay 안에
    await new Promise(r => setImmediate(r))
    const during = getLabelCacheStats()
    expect(during.inFlight).toBeGreaterThanOrEqual(1)

    await promise
    const after = getLabelCacheStats()
    expect(after.inFlight).toBe(0)
  })

  test('성공 캐시 후 동일 요청 100 회 동시 → cache hit 만 (build 0 회 추가)', async () => {
    // 첫 호출로 캐시 채움
    await labelcommand({ id: 'lbl1', data: {}, context: ctx })
    expect(buildCalls).toHaveLength(1)

    // 동시 100 회 — 모두 cache hit
    const results = await Promise.all(
      Array.from({ length: 100 }, () => labelcommand({ id: 'lbl1', data: {}, context: ctx }))
    )
    expect(buildCalls).toHaveLength(1)
    expect(new Set(results).size).toBe(1)
  })
})
